import type { RoadSection, RoadSectionFilter } from '../domain/Reference';

// Filters of the référentiel layer: criteria picked in the map's filter panel, and (optionally) the filters
// of the /reference table. Sections that do not match are dimmed on the map and left out of the figures.

// Stands for "no value" in a list filter, so sections missing the attribute can be found.
export const NO_VALUE = '__none__';

export const LIST_FILTER_FIELDS = ['category', 'roadType', 'urban', 'trucks', 'gestionary'] as const;
export type ListFilterField = typeof LIST_FILTER_FIELDS[number];
export const RANGE_FILTER_FIELDS = ['year', 'width'] as const;
export type RangeFilterField = typeof RANGE_FILTER_FIELDS[number];

export interface RangeFilter {
    min: number | null;
    max: number | null;
}

export interface ReferenceFilters {
    lists: Partial<Record<ListFilterField, string[]>>;
    ranges: Partial<Record<RangeFilterField, RangeFilter>>;
}

export const NO_FILTERS: ReferenceFilters = { lists: {}, ranges: {} };

const isRangeActive = (range: RangeFilter | undefined): range is RangeFilter =>
    range !== undefined && (range.min !== null || range.max !== null);

// One per field with a criterion, as counted on the "Filtres actifs" button.
export const activeFilterCount = (filters: ReferenceFilters): number =>
    Object.values(filters.lists).filter((values) => values !== undefined && values.length > 0).length +
    Object.values(filters.ranges).filter(isRangeActive).length;

const textOf = (value: string | number | null): string | null => {
    if (value === null) {
        return null;
    }
    const text = String(value).trim();

    return text === '' ? null : text;
};

export const matchesFilters = (section: RoadSection, filters: ReferenceFilters): boolean => {
    for (const field of LIST_FILTER_FIELDS) {
        const values = filters.lists[field];
        if (values && values.length > 0 && !values.includes(textOf(section[field]) ?? NO_VALUE)) {
            return false;
        }
    }
    for (const field of RANGE_FILTER_FIELDS) {
        const range = filters.ranges[field];
        if (!isRangeActive(range)) {
            continue;
        }
        const value = section[field];
        if (value === null || (range.min !== null && value < range.min) || (range.max !== null && value > range.max)) {
            return false;
        }
    }

    return true;
};

type TableItem = RoadSectionFilter['items'][number];

const tableValueOf = (section: RoadSection, field: string): string | number | null => {
    if (field === 'length') {
        return Math.abs(section.cumulEnd - section.cumulStart);
    }
    const value = ((section as unknown) as Record<string, unknown>)[field];

    return typeof value === 'string' || typeof value === 'number' || value === null ? value : null;
};

// null: the item cannot be evaluated here (unknown field or operator, or an unfinished filter), so it is
// ignored, as the table does for unfinished filters.
const matchesTableItem = (section: RoadSection, item: TableItem): boolean | null => {
    const { operator } = item;
    const raw = tableValueOf(section, item.field);
    const text = raw === null ? '' : String(raw).toLowerCase();
    if (operator === 'isEmpty') {
        return text === '';
    }
    if (operator === 'isNotEmpty') {
        return text !== '';
    }
    if (item.value === undefined || item.value === null || item.value === '') {
        return null;
    }
    if (operator === 'isAnyOf') {
        return Array.isArray(item.value) ? item.value.map((v) => String(v).toLowerCase()).includes(text) : null;
    }
    const wanted = String(item.value).toLowerCase();
    switch (operator) {
        case 'contains':
            return text.includes(wanted);
        case 'equals':
            return text === wanted;
        case 'startsWith':
            return text.startsWith(wanted);
        case 'endsWith':
            return text.endsWith(wanted);
        default:
    }
    const number = typeof raw === 'number' ? raw : null;
    const target = Number(String(item.value).replace(',', '.'));
    if (Number.isNaN(target)) {
        return null;
    }
    switch (operator) {
        case '>':
            return number !== null && number > target;
        case '>=':
            return number !== null && number >= target;
        case '<':
            return number !== null && number < target;
        case '<=':
            return number !== null && number <= target;
        case '=':
            return number !== null && number === target;
        case '!=':
            return number === null || number !== target;
        default:
            return null;
    }
};

// Applies the filter model of the /reference table to a section. Items combine with AND, or OR when the model
// says so; items that cannot be evaluated are ignored. Quick-filter text is not applied.
export const matchesTableFilter = (section: RoadSection, model: RoadSectionFilter): boolean => {
    const results = model.items
        .map((item) => matchesTableItem(section, item))
        .filter((result): result is boolean => result !== null);
    if (results.length === 0) {
        return true;
    }
    const orMode = (model as { logicOperator?: string }).logicOperator === 'or';

    return orMode ? results.some(Boolean) : results.every(Boolean);
};

export const hasUsableTableFilter = (model: RoadSectionFilter): boolean => model.items.length > 0;
